import { TranslocoService } from '@jsverse/transloco';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { GameId } from 'src/app/model/game/game-id';
import { gameIdToName } from 'src/app/model/game/game-id.utils';
import { RoomTimer } from 'src/app/model/room/room-timer';
import { RoomDisplayResult } from 'src/app/shared/types/room-display-result';
import { RoomResultStatus, RoomStatus } from 'src/app/shared/types/room-status';
import { Round } from 'src/app/shared/types/round';

export enum ScoreStatus {
  Finished,
  NoOpponent,
  OpponentNoScore,
  NoScore,
  NotStarted,
  InPlay,
  Processing,
}

export class ResultDrawerViewModel {
  gameName = 'Solitaire';
  subtitle?: string;
  ascii?: string;
  prize?: string;
  currencyIcon?: string;
  playerName?: string | null;
  playerId?: string | null;
  yourScore = '';
  opponentName?: string;
  opponentId?: string | null;
  opponentScore = '';
  scoreStatus: ScoreStatus = ScoreStatus.Finished;
  opponentStatus?: string;
  freeCta?: string;
  wonBountyAmount?: number | undefined;

  constructor(
    readonly roomTimer: RoomTimer | undefined,
    readonly roomId: string,
    readonly gameId: GameId,
    private statusColor: string,
    readonly isFree: boolean,
    private roomFee: string,
    private roomPrize: string,
    private roomScore: number | undefined,
    private roomStatus: RoomStatus,
    private roomRoundState: Round,
    private roomPlayerId: string | undefined | null,
    private roomPlayerName: string | undefined | null,
    private roomOpponentId: string | undefined | null,
    private roomOpponent: string | undefined,
    private roomOpponentScore: number | undefined,
    private roomOpponentRoundState: Round,
    readonly roomResult: RoomDisplayResult,
    readonly roomStatusResult: RoomResultStatus,
    private translocoService: TranslocoService,
    private themeService: ThemeService,
    readonly tournamentTitle?: string | null,
    private bountyAmount?: number,
  ) {
    this.initialize();
  }

  getGlowCss(): string {
    const map: Record<string, string> = {
      '--game-win': 'win',
      '--game-lose': 'lose',
      '--game-draw': 'draw',
      '--game-refund': 'refund',
      '--yellow-500': 'check',
      '--blue-500': 'refund',
    };
    return map[this.statusColor] || '';
  }

  private initialize(): void {
    this.gameName = gameIdToName(this.gameId, this.translocoService);
    this.subtitle = this.updateSubtitle();
    this.ascii = this.updateAscii();
    this.prize = this.updatePrize();
    this.yourScore = this.updateYourScore();
    this.playerName = this.roomPlayerName;
    this.playerId = this.roomPlayerId;
    this.opponentName = this.roomOpponent;
    this.opponentId = this.roomOpponentId;
    this.opponentScore = this.updateOpponentScore();
    this.scoreStatus = this.updateScoreStatus();
    this.opponentStatus = this.updateOpponentStatus();
    this.freeCta = this.updateFreeCta();
    this.wonBountyAmount = this.getWonBountyAmount();
  }

  private updateSubtitle(): string | undefined {
    switch (this.roomStatus) {
      case RoomStatus.Draw:
        return this.translocoService.translate('pages.clinch.result.subtitle-draw', {
          opponent: this.roomOpponent,
        });
      case RoomStatus.Lost:
        if (this.bountyAmount !== undefined)
          return this.translocoService.translate('pages.clinch.result.subtitle-lost-with-bounty');
        return this.translocoService.translate('pages.clinch.result.subtitle-lost', {
          opponent: this.roomOpponent,
        });
      case RoomStatus.Won:
        return this.translocoService.translate('pages.clinch.result.subtitle-win', {
          opponent: this.roomOpponent,
        });
      case RoomStatus.Cancelled:
        return this.translocoService.translate('pages.clinch.result.subtitle-cancelled');
      case RoomStatus.Open:
        return this.translocoService.translate('pages.clinch.inplay.check-later');
      case RoomStatus.OpponentToStart:
        return this.translocoService.translate('pages.clinch.inplay.not-started-title', {
          opponent: this.roomOpponent,
        });
      case RoomStatus.OpponentToFinish:
        return this.translocoService.translate('pages.clinch.inplay.in-progress', {
          opponent: this.roomOpponent,
        });
      default:
        return undefined;
    }
  }

  private updateAscii(): string {
    switch (this.roomStatus) {
      case RoomStatus.Cancelled:
      case RoomStatus.Draw:
        return '⏎';
      case RoomStatus.Lost:
        return '-';
      case RoomStatus.Won:
        return '+';
      default:
        return '';
    }
  }

  private updatePrize(): string | undefined {
    switch (this.roomStatus) {
      case RoomStatus.Won:
      case RoomStatus.Start:
      case RoomStatus.Open:
      case RoomStatus.OpponentToStart:
      case RoomStatus.OpponentToFinish:
      case RoomStatus.Processing:
        return this.roomPrize;
      default:
        return this.roomFee;
    }
  }

  private updateYourScore(): string {
    return this.roomScore !== undefined
      ? `${this.themeService.formatNumber(this.roomScore)}`
      : 'No score';
  }

  private updateOpponentScore(): string {
    return this.roomOpponentScore !== undefined
      ? `${this.themeService.formatNumber(this.roomOpponentScore)}`
      : 'No score';
  }

  private updateScoreStatus(): ScoreStatus {
    if (this.roomStatus === RoomStatus.Cancelled) {
      if (this.roomRoundState === Round.NotStarted) {
        return ScoreStatus.NotStarted;
      }
      if (!this.roomOpponent) {
        return ScoreStatus.NoOpponent;
      }
      if (this.roomOpponent && this.roomOpponentRoundState === Round.NotStarted) {
        return ScoreStatus.OpponentNoScore;
      }
      if (this.roomRoundState === Round.Started && this.roomScore === undefined) {
        return ScoreStatus.NoScore;
      }
    }
    if (
      this.roomStatus === RoomStatus.Finish ||
      this.roomStatus === RoomStatus.Open ||
      this.roomStatus === RoomStatus.OpponentToFinish ||
      this.roomStatus === RoomStatus.OpponentToStart
    ) {
      return ScoreStatus.InPlay;
    }
    if (this.roomStatus === RoomStatus.Processing) {
      return ScoreStatus.Processing;
    }
    return ScoreStatus.Finished;
  }

  private updateOpponentStatus(): string | undefined {
    switch (this.scoreStatus) {
      case ScoreStatus.NoOpponent:
        return this.translocoService.translate('pages.clinch.result.no-opponent-found');
      case ScoreStatus.OpponentNoScore:
        return this.translocoService.translate('pages.clinch.result.opponent-did-not-start');
      case ScoreStatus.NotStarted:
        return this.translocoService.translate('pages.clinch.result.match-not-started');
      case ScoreStatus.NoScore:
        return this.translocoService.translate('pages.clinch.result.you-did-not-submit-a-score');
      case ScoreStatus.InPlay:
        if (this.roomOpponent) {
          return this.translocoService.translate('pages.clinch.inplay.playing-title', {
            opponent: this.roomOpponent,
          });
        }
        return this.translocoService.translate('pages.clinch.inplay.open-title');
      case ScoreStatus.Processing:
        return this.translocoService.translate('pages.clinch.processing.subtitle');
      default:
        return '';
    }
  }

  private updateFreeCta(): string | undefined {
    switch (this.roomResult) {
      case RoomDisplayResult.Win:
        return this.translocoService.translate('pages.clinch.result.call-to-action-won');
      case RoomDisplayResult.Lose:
        return this.translocoService.translate('pages.clinch.result.call-to-action-lost');
      default:
        return this.translocoService.translate('pages.clinch.result.call-to-action-draw');
    }
  }

  private getWonBountyAmount(): number | undefined {
    if (this.roomStatus === RoomStatus.Won && this.bountyAmount) {
      return this.bountyAmount;
    }
    return undefined;
  }
}
